/**
 * The ONE translation between the wire's `progressionRules` document and the draft's readable map,
 * both directions.
 *
 * WIRE SHAPE: the exercise carries `progressionRules`, a MAP of `MeasurementField → rule document`,
 * each document discriminated on a `type` that is a `ProgressionType` NAME (`Static` /
 * `PreviousExecution`). The key is the column the rule writes; a field with no entry rides flat.
 *
 * The draft holds the same map in this app's own vocabulary (`progressionRules.ts`): lower-cased
 * field keys, and `null` where the wire says "absent".
 *
 * ── TOTAL, and that is the whole point of splitting it out ───────────────────────────────────
 * Every entry the wire carries lands on the draft and every entry the draft carries goes back out,
 * **including rules on fields this build has no editor for**. There is no filtering step to forget,
 * so a load → edit → save of a document richer than the screen is non-destructive. A shipped client
 * cannot be force-updated: this app has to give back what it was handed.
 *
 * ── A document this build CAN read may still carry more than it knows ────────────────────────
 * A newer backend adding an optional property to a rule type this build understands is not an
 * unreadable document — it opens, it means what it always meant, and the extra field is invisible
 * here. Every save from this client is the whole tree, so re-emitting only the four fields it knows
 * would DELETE that property, silently, for a trainer who never touched the rule.
 *
 * So a readable entry keeps the document it arrived as (`wireDocument`), and `ruleToWire` writes
 * the fields this build owns OVER it. The carried half is dropped when the trainer changes the
 * rule's TYPE, because a `banded` from a `PreviousExecution` document has no meaning on a `Static`
 * one and would travel as a field nobody authored.
 *
 * **Mobile does not do this** — its mapper emits the four fields and nothing else. Doing it here is
 * additive and strictly safer: this client gives back everything it was handed, which is the same
 * promise `unreadable` already makes one level up.
 *
 * ── A document this build cannot READ still has to come back out ─────────────────────────────
 * Two shapes reach here that `ProgressionRule` has no way to hold: a document whose `type` this
 * build has never heard of, and an entry keyed on a measurement field it cannot parse. Both are what
 * a NEWER backend hands an OLDER client, and a shipped client cannot be force-updated — so neither
 * is coerced (that would present a rule the app cannot show as "nothing progresses here", and the
 * next save would make the lie true) and neither is dropped (that would delete the trainer's rule on
 * the next whole-tree save, silently).
 *
 * They travel beside the readable map instead, as `ReadProgressionRules.unreadable`: the wire value
 * verbatim, never inspected, re-emitted byte for byte. That is the `baseVersion` contract applied to
 * one map entry, and it is what the opaque period bought before the port — kept rather than spent.
 * A surface may NAME such an entry (the card's foot row prints the server's own spelling) but
 * nothing may claim to know what it does.
 *
 * The readable half wins a collision: authoring a rule on a column whose stored document was
 * unreadable REPLACES it, which is the only reading of a trainer deliberately overwriting it.
 *
 * ── Ported from `move-with-me-frontend/.../plans/builder/model/progressionWire.ts` ────────────
 * The rule half is 1:1. The OVERRIDE half is not here: this app's `ProgressionOverride` is a flat
 * row of eleven prescribed columns rather than mobile's `values` map, and its emitter has always
 * lived in `progressionRuleWire.ts` beside the rest of the save path. Two mappers over the same
 * eleven columns would be two places for them to disagree.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type {
  TrainWithMeDataEnumsMeasurementField,
  TrainWithMeModulesPlansDtosRequestsPostExerciseTemplateInputProgressionRules,
  TrainWithMeModulesPlansDtosResponsesExerciseTemplateResponseProgressionRules,
} from '~/shared/api/generated/model';
import {
  ALL_MEASUREMENT_FIELD_KEYS,
  measurementFieldToWire,
  parseMeasurementField,
  type MeasurementFieldKey,
} from '~/shared/measurement';

import type { ProgressionDeltaUnit, ProgressionRule, ProgressionRules } from './progressionRules';

/**
 * The wire's rule map.
 *
 * The REQUEST side is the open one — an index signature rather than the response's nine named keys —
 * so a field the backend adds tomorrow survives a round trip without a regeneration here. The
 * response side assigns into it directly; that was verified against the generated types.
 */
export type ProgressionRulesWire =
  | TrainWithMeModulesPlansDtosResponsesExerciseTemplateResponseProgressionRules
  | TrainWithMeModulesPlansDtosRequestsPostExerciseTemplateInputProgressionRules;

/** One wire rule document — the union every value of the map is. */
type ProgressionRuleWire = NonNullable<
  NonNullable<TrainWithMeModulesPlansDtosResponsesExerciseTemplateResponseProgressionRules>['Weight']
>;

/**
 * The wire's delta unit → the draft's. Anything but the one spelling that compounds reads as
 * `Absolute`, which is both the backend's own default for the key and the only reading a column
 * other than the load can take.
 */
function deltaUnitFromWire(value: string | null | undefined): ProgressionDeltaUnit {
  return value === 'Percent' ? 'Percent' : 'Absolute';
}

/**
 * One wire rule document → the draft's rule, or null when this build does not know its `type`.
 *
 * A rule whose type is unknown is never COERCED. `rulesFromWire` is what does not drop it either:
 * the null sends it to the `unreadable` half, where it is held verbatim and re-emitted, so the
 * trainer's rule survives a save from a client that cannot say what it does. What such a rule does
 * NOT get is a projection — the matrix shows the column flat, which is the honest reading of "this
 * build cannot compute what that rule will produce".
 *
 * That is the runtime BELT, and it should never be reached. The compile-time brace is the `never`
 * assignment below: no `default` label, so the day the backend adds a third `ProgressionType` and
 * codegen widens the union, this stops building instead of quietly dropping every rule of the new
 * kind. The two are not redundant — the union describes the contract this build was compiled
 * against, and a running app can be handed a document from a newer one.
 */
function ruleFromWire(document: ProgressionRuleWire): ProgressionRule | null {
  switch (document.type) {
    case 'PreviousExecution': {
      // TWO NULLS, AND THEY ARE NOT THE SAME ANSWER. An ABSENT band is a legal, complete rule —
      // "advance when the client delivers the whole set" — and reads as `banded: null`. A band that
      // is PRESENT but names a column this build does not know is a rule the app can neither show
      // nor re-emit, so it is dropped WHOLE: coercing it to "no band" would rewrite the rule's
      // meaning on screen, and the next save would make that rewrite true.
      const raw = document.banded;
      // Held for the re-emit, never read. See the header.
      const banded = raw === null || raw === undefined ? null : parseMeasurementField(raw);
      if (raw !== null && raw !== undefined && banded === null) return null;
      return {
        wireDocument: document,
        type: 'PreviousExecution',
        banded,
        // The document's `Delta` is a plain `decimal` on the backend, so a missing one is not "no
        // step" — it is a payload that never stated the field. Zero is what the backend's own
        // deserialiser reads it as, and reading it the same way here keeps a load → save round trip
        // of such a document a no-op.
        delta: document.delta ?? 0,
        deltaUnit: deltaUnitFromWire(document.deltaUnit),
      };
    }
    case 'Static':
      return {
        wireDocument: document,
        type: 'Static',
        delta: document.delta ?? null,
        deltaUnit: deltaUnitFromWire(document.deltaUnit),
      };
    default: {
      const unreadable: never = document;
      void unreadable;
      return null;
    }
  }
}

/**
 * The draft's rule → its wire document. A key the wire says is absent is OMITTED, never sent null.
 *
 * The unit ALWAYS ships, on both families and on a flat rule too: the backend requires the key
 * outright and refuses a document without it, which is what makes "the delta is in this column's own
 * units" a thing the request STATES (`Absolute`) rather than a thing the reader infers.
 */
/** The four keys this build authors. Everything else in a stored document is carried, not owned. */
const AUTHORED_KEYS: readonly string[] = ['type', 'banded', 'delta', 'deltaUnit'];

/**
 * The properties the stored document carried that this build has no name for, or nothing.
 *
 * Guarded on the TYPE: a document is only carried under a rule of the kind it arrived as. A trainer
 * who turns a `PreviousExecution` rule into a `Static` one is authoring a different document, and
 * the old one's extra fields describe a rule that no longer exists.
 */
function carriedFields(rule: ProgressionRule): Readonly<Record<string, unknown>> {
  const document = rule.wireDocument;
  if (document === undefined || document.type !== rule.type) return {};
  return Object.fromEntries(
    Object.entries(document).filter(([key]) => !AUTHORED_KEYS.includes(key)),
  );
}

function ruleToWire(rule: ProgressionRule): ProgressionRuleWire {
  const carried = carriedFields(rule);
  if (rule.type === 'PreviousExecution') {
    return {
      ...carried,
      type: 'PreviousExecution',
      // OMITTED, never null. The backend reads an absent key and an explicit null as the same thing
      // and it writes with `WhenWritingNull` — so a document it stored without the key must come
      // back out without it, or a load → save round trip of an unbanded rule stops being a no-op.
      ...(rule.banded === null
        ? {}
        : { banded: measurementFieldToWire(rule.banded) as TrainWithMeDataEnumsMeasurementField }),
      // Always stated — the receiving document's `Delta` is not nullable, so an omitted key is a
      // zero the trainer never wrote. The draft's own type guarantees there is one to state.
      delta: rule.delta,
      deltaUnit: rule.deltaUnit,
    };
  }
  // Static — a null delta is a flat rule, and it still states the unit its (absent) delta reads in.
  return { ...carried, type: 'Static', delta: rule.delta, deltaUnit: rule.deltaUnit };
}

/** Everything one exercise's stored document holds: what this build can read, and what it cannot. */
export interface ReadProgressionRules {
  readonly rules: ProgressionRules;
  /**
   * The entries this build has no reading for, keyed by the WIRE field name and holding the wire
   * value verbatim. Never inspected, never edited, emitted byte for byte. Empty for every document
   * the current contract can produce.
   */
  readonly unreadable: Readonly<Record<string, unknown>>;
}

/** Nothing stored at all — the state a freshly-picked exercise and a legacy template share. */
export const NO_STORED_RULES: ReadProgressionRules = { rules: {}, unreadable: {} };

/**
 * The wire's rule map → the draft's. A missing or null document is an exercise where nothing
 * progresses, which is exactly what a legacy template stored before this shape existed and what the
 * backend reads an empty map as — so a load → save round trip of one is a no-op rather than an
 * invented flat rule.
 */
export function rulesFromWire(wire: ProgressionRulesWire | null | undefined): ReadProgressionRules {
  if (wire === null || wire === undefined) return NO_STORED_RULES;
  const rules: Partial<Record<MeasurementFieldKey, ProgressionRule>> = {};
  const unreadable: Record<string, unknown> = {};
  for (const [wireField, document] of Object.entries(wire)) {
    if (document === null || document === undefined) continue;
    const field = parseMeasurementField(wireField);
    const rule = field === null ? null : ruleFromWire(document);
    if (field !== null && rule !== null) rules[field] = rule;
    else unreadable[wireField] = document;
  }
  return { rules, unreadable };
}

/**
 * The draft's rule map → the wire document. An exercise with no rules emits an empty map, which the
 * backend reads as "nothing progresses".
 *
 * The readable rules walk the DICTIONARY rather than the map's own keys, so the emitted document is
 * in the one canonical field order whatever order the rules were authored in — the same R4 reason
 * every other field list in this app is sorted. The unreadable entries follow, minus any column the
 * trainer has since authored a real rule on.
 */
export function rulesToWire(stored: ReadProgressionRules): ProgressionRulesWire {
  const wire: Record<string, unknown> = {};
  const claimed = new Set<string>();
  for (const field of ALL_MEASUREMENT_FIELD_KEYS) {
    const rule = stored.rules[field];
    if (rule === undefined) continue;
    const wireField = measurementFieldToWire(field);
    claimed.add(wireField);
    wire[wireField] = ruleToWire(rule);
  }
  for (const [wireField, document] of Object.entries(stored.unreadable)) {
    if (!claimed.has(wireField)) wire[wireField] = document;
  }
  return wire;
}
